/**
 * LandingPricing — replaces the hard-coded 3-tier grid with the real
 * plans configured by Admins.
 *
 * Data sources
 * ------------
 *   • ``GET /api/public/landing/geo``   — auto-detect country from
 *     edge headers (CF/Vercel) with Accept-Language fallback.
 *   • ``GET /api/public/landing/plans`` — primary plans active in that
 *     country, with prices already localized to the country currency.
 *
 * UX
 * --
 *   • A country-flag chip lets the visitor override the auto-detected
 *     country (e.g. an Argentine browsing from a VPN in the US).
 *   • Each plan card carries the localized currency + price + features.
 *   • The middle plan is highlighted as "most popular" — the convention
 *     is to mark the second tier when there are 3+ plans, falling back
 *     to the first when there's only 1-2.
 *   • Empty/loading states are graceful: skeleton cards on load, a
 *     "Falling back to representative pricing" banner if the API errors
 *     (so the landing never looks broken).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Sparkles, ChevronDown, Loader2 } from "lucide-react";
import { Button } from "../../components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "../../components/ui/dropdown-menu";

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

// ----- helpers --------------------------------------------------------------

function formatMoney(amount, currency, locale) {
  if (amount == null) return "—";
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
      maximumFractionDigits: amount >= 100 ? 0 : 2,
    }).format(amount);
  } catch {
    return `${currency} ${Number(amount).toLocaleString(locale)}`;
  }
}

// Per-locale-ish defaults for Intl formatting.
const LOCALE_BY_COUNTRY = {
  CO: "es-CO", MX: "es-MX", AR: "es-AR", CL: "es-CL",
  PE: "es-PE", UY: "es-UY", BR: "pt-BR",
};

// Feature catalog → user-facing strings. Whatever feature key the
// backend ships we map here; missing keys are silently dropped to
// keep the cards clean.
const FEATURE_LABELS_ES = {
  telegram_bot: "Bot Telegram",
  whatsapp_bot: "Bot WhatsApp",
  weekly_digest: "Resumen semanal",
  fx_alerts: "Alertas FX",
  recurring_movements: "Movimientos recurrentes",
  basic_reports: "Reportes básicos",
  advanced_reports: "Reportes avanzados",
  cash_flow_weekly: "Cash-flow semanal",
  advisor_chat: "Asistente IA",
  ai_recommendations: "Recomendaciones IA",
  scenarios: "Escenarios what-if",
  calendar_heatmap: "Mapa de calor",
  amortization: "Amortización",
  smart_alerts: "Alertas inteligentes",
  roles_permissions: "Roles & permisos",
  accountant_readonly: "Acceso contador",
  advanced_exports: "Exportes avanzados",
  basic_api: "API básica",
  audit_log: "Registro de auditoría",
  image_extractions: "Recibos IA",
};

const FEATURE_LABELS_EN = {
  telegram_bot: "Telegram bot",
  whatsapp_bot: "WhatsApp bot",
  weekly_digest: "Weekly digest",
  fx_alerts: "FX alerts",
  recurring_movements: "Recurring movements",
  basic_reports: "Basic reports",
  advanced_reports: "Advanced reports",
  cash_flow_weekly: "Weekly cash-flow",
  advisor_chat: "AI assistant",
  ai_recommendations: "AI recommendations",
  scenarios: "What-if scenarios",
  calendar_heatmap: "Calendar heatmap",
  amortization: "Amortization",
  smart_alerts: "Smart alerts",
  roles_permissions: "Roles & permissions",
  accountant_readonly: "Accountant access",
  advanced_exports: "Advanced exports",
  basic_api: "Basic API",
  audit_log: "Audit log",
  image_extractions: "AI Receipts",
};

const FEATURE_LABELS = { es: FEATURE_LABELS_ES, en: FEATURE_LABELS_EN, pt: FEATURE_LABELS_ES };

// Resolve which plan should wear the "Most popular" crown for the
// current billing period. The admin sets `popular_monthly` and
// `popular_annual` per plan in the admin form, and the backend
// enforces uniqueness (only one plan per kind per period). If the
// admin hasn't picked any (legacy data, fresh seed), we fall back to
// the historical heuristic: ascending-price sort, then highlight the
// first paid plan as the natural upgrade target.
function markPopular(plans, billingPeriod = "monthly") {
  if (!plans?.length) return [];
  const sorted = [...plans].sort((a, b) =>
    (Number(a.price_amount) || 0) - (Number(b.price_amount) || 0),
  );
  const flag = billingPeriod === "annual" ? "popular_annual" : "popular_monthly";
  const adminPicked = sorted.findIndex((p) => p[flag]);
  if (adminPicked >= 0) {
    return sorted.map((p, i) => ({ ...p, _popular: i === adminPicked }));
  }
  // Fallback heuristic when no admin pick exists yet.
  let featuredIdx = 0;
  const firstPaid = sorted.findIndex((p) => (p.price_amount || 0) > 0);
  if (sorted.length >= 3) {
    featuredIdx = firstPaid >= 0 ? firstPaid : 1;
  } else if (sorted.length === 2) {
    featuredIdx = firstPaid >= 0 ? firstPaid : 1;
  }
  return sorted.map((p, i) => ({ ...p, _popular: i === featuredIdx }));
}


// ----- main component -------------------------------------------------------

export function LandingPricing({ lang = "es" }) {
  const isEs = lang === "es";
  const isEn = lang === "en";

  const [country, setCountry] = useState(null);
  const [supported, setSupported] = useState([]);
  const [plans, setPlans] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const featureLabels = FEATURE_LABELS[lang] || FEATURE_LABELS.es;

  // Billing period toggle — sticky across landing ↔ in-app /pricing
  // via the same localStorage key Pricing.jsx writes ("lumfi.billing_
  // period.v1"). A logged-in visitor who flips this on the landing
  // arrives at the in-app checkout with annual already selected.
  const BILLING_PERIOD_KEY = "lumfi.billing_period.v1";
  const [billingPeriod, setBillingPeriodState] = useState(() => {
    try {
      const v = localStorage.getItem(BILLING_PERIOD_KEY);
      return v === "annual" ? "annual" : "monthly";
    } catch { return "monthly"; }
  });
  const setBillingPeriod = (v) => {
    setBillingPeriodState(v);
    try { localStorage.setItem(BILLING_PERIOD_KEY, v); } catch { /* noop */ }
  };
  const anyAnnual = (plans || []).some((p) => p.annual_price_amount > 0);

  // Step 1 — detect country once. Cache in sessionStorage to avoid
  // bouncing the edge endpoint on every soft route change.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const cached = sessionStorage.getItem("lumfi.landing.geo");
        if (cached) {
          const j = JSON.parse(cached);
          if (!cancelled) {
            setCountry(j.country);
            setSupported(j.supported || []);
          }
          return;
        }
        const r = await fetch(`${API_BASE}/api/public/landing/geo`, { credentials: "omit" });
        const j = await r.json();
        if (cancelled) return;
        setCountry(j.country);
        setSupported(j.supported || []);
        sessionStorage.setItem("lumfi.landing.geo", JSON.stringify(j));
      } catch {
        if (!cancelled) setCountry("CO");
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Step 2 — fetch plans whenever the country changes.
  useEffect(() => {
    if (!country) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const r = await fetch(
          `${API_BASE}/api/public/landing/plans?country=${country}`,
          { credentials: "omit" }
        );
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const j = await r.json();
        if (!cancelled) setPlans(j.plans || []);
      } catch (e) {
        if (!cancelled) setError(e.message || "error");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [country]);

  const handleCountryChange = (cc) => {
    setCountry(cc);
    sessionStorage.setItem("lumfi.landing.geo", JSON.stringify({
      country: cc,
      supported,
    }));
  };

  const activeCountryMeta = supported.find((c) => c.cc === country);
  const locale = LOCALE_BY_COUNTRY[country] || "es-CO";

  return (
    <div data-testid="landing-pricing-dynamic">
      {/* Country switcher */}
      {supported.length > 0 && (
        <div className="flex items-center justify-center gap-2 mb-8 flex-wrap">
          <span className="text-xs text-slate-500">
            {isEs ? "Precios en" : isEn ? "Prices in" : "Preços em"}
          </span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button /* design-system: ALLOW legacy-todo */
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-slate-200 hover:border-emerald-400 transition-colors text-sm font-semibold shadow-sm"
                data-testid="landing-pricing-country-toggle"
              >
                <span className="text-base leading-none">{activeCountryMeta?.flag || "🌎"}</span>
                <span>{activeCountryMeta?.label || country || "—"}</span>
                <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="center" className="w-48">
              <DropdownMenuLabel className="text-[10px] uppercase tracking-wider">
                {isEs ? "Tu país" : isEn ? "Your country" : "Seu país"}
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {supported.map((c) => (
                <DropdownMenuItem
                  key={c.cc}
                  onClick={() => handleCountryChange(c.cc)}
                  data-testid={`landing-pricing-country-${c.cc}`}
                  className="cursor-pointer gap-2"
                >
                  <span className="text-base">{c.flag}</span>
                  <span className="flex-1">{c.label}</span>
                  <span className="text-[10px] text-slate-400 font-mono">{c.currency}</span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}

      {/* Billing period toggle — only visible when ≥1 plan exposes
          annual pricing for the active country. Otherwise the chip
          would lie about what's available. */}
      {anyAnnual && (
        <div className="flex items-center justify-center mb-8" data-testid="landing-billing-period">
          <div className="inline-flex items-center bg-white border border-slate-200 rounded-full p-1 shadow-sm">
            {[
              { v: "monthly", labelEs: "Mensual",  labelEn: "Monthly", labelPt: "Mensal" },
              { v: "annual",  labelEs: "Anual",    labelEn: "Annual",  labelPt: "Anual" },
            ].map((opt) => {
              const isActive = billingPeriod === opt.v;
              return (
                <button /* design-system: ALLOW legacy-todo */
                  key={opt.v}
                  type="button"
                  onClick={() => setBillingPeriod(opt.v)}
                  data-testid={`landing-billing-period-${opt.v}`}
                  className={`px-5 py-2 text-sm font-semibold rounded-full transition-all ${
                    isActive
                      ? "bg-slate-900 text-white shadow-sm"
                      : "text-slate-600 hover:text-slate-900"
                  }`}
                >
                  {isEs ? opt.labelEs : isEn ? opt.labelEn : opt.labelPt}
                  {opt.v === "annual" && !isActive && (
                    <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-emerald-100 text-emerald-700 uppercase tracking-wider">
                      {isEs ? "Ahorro" : isEn ? "Save" : "Economia"}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Plans grid */}
      {loading && (
        <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
          {[1, 2, 3].map((i) => (
            <div key={i} className="rounded-3xl p-8 bg-white border border-cream-300 animate-pulse h-[420px]" />
          ))}
        </div>
      )}

      {!loading && error && (
        <div className="text-center py-8 text-amber-700 bg-amber-50/60 border border-amber-200 rounded-2xl px-6" data-testid="landing-pricing-error">
          <p className="text-sm">
            {isEs
              ? "No pudimos cargar los planes en este momento. "
              : isEn
              ? "We couldn't load plans right now. "
              : "Não foi possível carregar os planos agora. "}
            <Link to="/pricing" className="font-semibold underline">
              {isEs ? "Ver precios completos" : isEn ? "View full pricing" : "Ver preços completos"}
            </Link>
          </p>
        </div>
      )}

      {!loading && !error && plans?.length === 0 && (
        <div className="text-center py-10 text-slate-500" data-testid="landing-pricing-empty">
          {isEs
            ? `Aún no hay planes disponibles para ${activeCountryMeta?.label}. Prueba otro país.`
            : `No plans available for ${activeCountryMeta?.label} yet. Try another country.`}
        </div>
      )}

      {!loading && !error && plans?.length > 0 && (
        <div
          className={`grid gap-6 lg:gap-8 ${
            plans.length === 1 ? "max-w-md mx-auto" :
            plans.length === 2 ? "md:grid-cols-2 max-w-3xl mx-auto" :
            plans.length === 3 ? "md:grid-cols-3" :
            "md:grid-cols-2 lg:grid-cols-4"
          }`}
        >
          {markPopular(plans, billingPeriod).map((p, i) => (
            <PlanCard
              key={p.id}
              plan={p}
              index={i}
              locale={locale}
              featureLabels={featureLabels}
              isEs={isEs}
              isEn={isEn}
              billingPeriod={billingPeriod}
            />
          ))}
        </div>
      )}

      {/* Legal hint */}
      <p className="text-center text-[11px] text-slate-400 mt-10 max-w-2xl mx-auto">
        {isEs
          ? "Los precios mostrados están sujetos a impuestos locales según el país. Puedes cambiar o cancelar tu plan en cualquier momento desde tu cuenta."
          : isEn
          ? "Prices shown are subject to local taxes. You can change or cancel your plan anytime from your account."
          : "Os preços estão sujeitos a impostos locais. Você pode alterar ou cancelar seu plano a qualquer momento."}
      </p>
    </div>
  );
}


function PlanCard({ plan, index, locale, featureLabels, isEs, isEn, billingPeriod = "monthly" }) {
  const featured = plan._popular;
  const monthly = plan.price_amount;
  const annual = plan.annual_price_amount;
  const currency = plan.currency || plan.price_currency || "USD";
  const isAnnual = billingPeriod === "annual" && annual > 0;
  const price = isAnnual ? annual : monthly;
  const isFree = !monthly || monthly === 0;
  const savingsPct = isAnnual ? Number(plan.annual_discount_pct || 0) : 0;
  // Pick the lang code for i18n lookup.
  const lang = isEs ? "es" : isEn ? "en" : "pt";
  // Admin-authored display features take precedence over the internal
  // entitlement-key feature list. If empty we fall back to the existing
  // i18n-mapped entitlement labels so legacy plans keep working.
  const adminBullets = (plan.display_features_i18n?.[lang] || []).slice(0, 6);
  const fallbackBullets = (plan.features || [])
    .slice(0, 6)
    .map((f) => featureLabels[f] || f.replace(/_/g, " "));
  const features = adminBullets.length > 0 ? adminBullets : fallbackBullets;
  const ctaTo = "/register";

  return (
    <div
      data-testid={`landing-pricing-plan-${index}`}
      data-plan-id={plan.id}
      className={`relative rounded-3xl p-8 lg:p-10 transition-all duration-300 flex flex-col ${
        featured
          ? "bg-slate-900 text-white border border-emerald-500/40 lg:scale-[1.03] shadow-[0_30px_80px_-30px_rgba(15,23,42,0.45)]"
          : "bg-white text-slate-900 border border-cream-300 hover:border-emerald-300 hover:shadow-[0_18px_48px_-24px_rgba(15,23,42,0.18)]"
      }`}
    >
      {featured && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500 text-white text-[10px] font-bold uppercase tracking-[0.18em]" data-testid="landing-pricing-popular-badge">
          <Sparkles className="h-3 w-3" />
          {isEs ? "Más popular" : isEn ? "Most popular" : "Mais popular"}
        </span>
      )}

      <h3 className="font-serif-display text-3xl font-medium">{plan.name}</h3>
      <p className={`mt-3 text-sm leading-relaxed min-h-[40px] ${featured ? "text-slate-300" : "text-slate-600"}`}>
        {planTagline(plan, { isEs, isEn })}
      </p>

      <div className="mt-7 flex items-baseline gap-1 flex-wrap">
        <span className="font-mono-num text-4xl font-medium">
          {isFree ? (isEs ? "Gratis" : isEn ? "Free" : "Grátis") : formatMoney(price, currency, locale)}
        </span>
        {!isFree && (
          <span className={`text-xs ${featured ? "text-slate-400" : "text-slate-500"}`}>
            /{isAnnual
              ? (isEs ? "año" : isEn ? "yr" : "ano")
              : (isEs ? "mes" : isEn ? "mo" : "mês")}
          </span>
        )}
        {isAnnual && savingsPct > 0 && (
          <span
            className={`ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
              featured ? "bg-emerald-500/20 text-emerald-300" : "bg-emerald-100 text-emerald-700"
            }`}
            data-testid={`landing-pricing-savings-${index}`}
          >
            {isEs ? `−${savingsPct}%` : `−${savingsPct}%`}
          </span>
        )}
      </div>

      {features.length > 0 && (
        <ul className="mt-6 space-y-2 text-sm flex-1" data-testid={`landing-pricing-features-${index}`}>
          {features.map((f, idx) => (
            <li key={`${idx}-${f}`} className="flex items-start gap-2">
              <Check className={`h-4 w-4 mt-0.5 shrink-0 ${featured ? "text-emerald-400" : "text-emerald-600"}`} />
              <span className={featured ? "text-slate-200" : "text-slate-700"}>
                {f}
              </span>
            </li>
          ))}
        </ul>
      )}

      <Link to={ctaTo} className="block mt-8" onClick={() => {
        // iter-205 — funnel: pricing_clicked from the public landing.
        try {
          // dynamic import to avoid bundling the api lib on critical path
          import("../../lib/demoEvents").then((m) => m.trackDemoEvent("pricing_clicked", {
            extra: { plan_name: plan.name, plan_id: plan.id, is_free: isFree },
          })).catch(() => {});
        } catch { /* ignore */ }
      }}>
        <Button
          data-testid={`landing-pricing-cta-${index}`}
          className={`w-full rounded-full ${
            featured
              ? "bg-emerald-500 hover:bg-emerald-400 text-white"
              : "bg-white text-slate-900 border border-slate-300 hover:border-slate-900"
          }`}
          variant={featured ? "default" : "outline"}
        >
          {/* iter-205 — Admin-authored per-language CTA wins when set. */}
          {(plan.cta_i18n?.[lang] || "").trim() || (
            isFree
              ? (isEs ? "Empezar gratis" : isEn ? "Start free" : "Começar grátis")
              : (isEs ? "Suscribirme" : isEn ? "Subscribe" : "Assinar")
          )} <ArrowRight className="h-4 w-4 ml-1" />
        </Button>
      </Link>
    </div>
  );
}


// Marketing taglines per plan tier — keyed by plan name (case-insensitive)
// or by `kind`+`tier` fallback. Admin can rename plans freely without
// breaking the page; we just gracefully fall back to a tier-based default.
//
// Admin-authored ``description_i18n[lang]`` takes precedence over every
// hardcoded tagline below. That's the path that's editable from
// /admin → Billing → Plans → Edit → "Public landing copy".
function planTagline(plan, { isEs, isEn }) {
  const lang = isEs ? "es" : isEn ? "en" : "pt";
  const admin = (plan.description_i18n?.[lang] || "").trim();
  if (admin) return admin;

  const name = (plan.name || "").toLowerCase().trim();
  const isFree = !plan.price_amount || plan.price_amount === 0;

  // Free tier — any name (Control, Free, Starter, etc.)
  if (isFree || name === "control" || name === "free" || name === "starter") {
    return isEs ? "Arranca con lo esencial. Tu cash, tus cobros, tu límite seguro."
      : isEn ? "Start with the essentials. Your cash, receivables and safe limit."
      : "Comece com o essencial. Seu caixa, recebíveis e limite seguro.";
  }

  // Business tier — match before "pro" to avoid the "pro" substring trap.
  if (name === "negocio" || name === "business" || name === "team" || plan.tier === "business") {
    return isEs ? "Para microequipos: roles, contador y exportes avanzados."
      : isEn ? "For micro-teams: roles, accountant access and advanced exports."
      : "Para micro-equipes: papéis, acesso para contador e exportações avançadas.";
  }

  // "Pro" tier — entry paid plan (when separate from Pilot).
  if (name === "pro") {
    return isEs ? "Para freelance que empieza a cobrar fijo. IA básica + alertas."
      : isEn ? "For freelancers starting to bill regularly. Basic AI + alerts."
      : "Para freelancers que começam a faturar regularmente. IA básica + alertas.";
  }

  // Mid tier — Pilot
  if (name === "pilot" || plan.tier === "pro") {
    return isEs ? "Suma IA, alertas FX y predicciones. Para freelance que cobra en serio."
      : isEn ? "Add AI, FX alerts and forecasts. For freelancers earning real money."
      : "Adicione IA, alertas FX e previsões. Para freelancers que faturam de verdade.";
  }
  return plan.description_short || "";
}
